<app-page-header title="Custom Molded Rubber Parts & Seals"
  subtitle="Custom Parts and Design Assistance for Your Unique Application."
  image="url(assets/site/images/custom-plastic.webp)">
</app-page-header>

<div id="content" class="container">
  <p>When it comes to complex applications, two seals are rarely the same. Custom molded rubber parts might be the
    answer.</p>
  <p>Marco Rubber's 40+ years of experience, dedication to the art of sealing, and ISO 9001 certification make it easy
    to get the custom rubber parts you need.</p>
  <p>We offer custom transfer, injection, and compression molding in any material or profile on the market. Our team of
    experts offers rapid problem-solving for cost-effective custom solutions, often with no additional tooling costs.
    The goal is to get you a long-lasting solution that reduces downtime, improves efficiency, and helps your bottom
    line.</p>
  <div class="row-column-responsive justify-content-between align-center">
    <img width="208" src="/assets/site/images/Rubber-Bellow.webp" alt="Rubber Bellow">
    <img width="208" src="/assets/site/images/Custom-Molded-FFKM-Bushing.webp" alt="Custom Molded FFKM Bushing">
    <img width="208" src="/assets/site/images/Custom-Molded-FFKM-Washer-Isolation-Pad.webp"
      alt="Custom Molded FFKM Washer Isolation Pad">
    <img width="208" src="/assets/site/images/Rubber-Stopper-Plug.webp" alt="Rubber Stopper Plug">
  </div>
  <h3>Our Custom Rubber Molding Process</h3>
  <div class="scroll-x">
    <img src="/assets/site/images/custom-process.webp" />
  </div>
  <br />
  <p><a [routerLink]="['/custom-molding-process']">Learn more about our custom process.</a></p>
  <h2>Custom Moldable Rubber Materials</h2>
  <p>Here at Marco Rubber, we are innovators. We like to see what others don't and find solutions they couldn't think
    of. This extends to our custom rubber solutions - especially the materials we use. We offer a wide range of
    materials and can customize your formulation to include one or more materials to meet your application's demands.
  </p>
  <p>Moreover, we can customize the profile to your exact needs. With expanded options like vee-packing sets, we are not
    an average o-ring supplier. We can match your application's complexity with a seal designed to last.</p>
  <p>To design that seal, we start with the material. A few of the most common customizable materials include FFKM, NBR,
    and EPDM. While these are commonly used in complex seals, we can capitalize on any material available today. </p>
  <h3>Perfluoroelastomers (FFKM)</h3> 
  <p>This fluorinated, carbon-based synthetic rubber is nearly unmatched. Our high-performance <a
      [routerLink]="['/markez-ffkm.htm']">FFKM material, Markez®</a>,
      offers a cost-effective alternative to many expensive options. Markez® is durable, versatile, and chemical- and
    temperature-resistant. Available in an impressive array of formulas, Markez® is suitable for many applications. We
    even offer ultraclean formulas for semiconductors and pharmaceuticals (or anyone else who needs cleanroom-grade
    manufacturing and packaging). </p>
  <h4>Benefits of FFKM</h4>
  <ul class="responsive-grid-list">
    <li>High temperature ratings, up to approximately 635°F (335°C), and lower temperatures, going down to -49°F
      (-45°C).</li>
    <li>Excellent gas and liquid permeation resistance.</li>
    <li>Self-extinguishing and non-flammable in air.</li>
    <li>Good mechanical properties, improving sealing performance.</li>
    <li>Nearly universal chemical compatibility.</li>
    <li>Excellent weather ability and ozone resistance.</li>
    <li>Low outgassing properties (low extractables).</li>
    <li>Suitability for explosive decompression, CIP (Clean in Place), SIP (Steam in Place), USP Class VI, and FDA.</li>
  </ul>
</div>
<app-cta-full-width contentOverride="true" image="url(assets/site/images/cta2.webp)">
  <div class="grid-column-span-2">
    <h4>Markez® Next Generation FFKM Perfluoroelastomer</h4>
    <h5 class="text-secondary">An Advanced Material For Chemically Resistant, High Pressure & High Temperature
      O-Rings.</h5>
    <p class="text-black">Markez® FFKM delivers similar performance to major FFKM brands, with temperature resistance up
      to 625 °F, at a
      significantly lower cost.</p>
    <div class="add-flex flex-row gap-20 mb-20">
      <a class="btn btn-primary add-flex justify-content-center" [routerLink]="['/markez-o-rings.htm']">Explore
        Markez</a>
      <a [routerLink]="['/markez-o-rings.htm']">
        <img alt="Marco Rubber Markez Perfluoroelastomers Logo" src="assets/site/images/markez-logo-transparent.webp" />
      </a>
    </div>
  </div>
</app-cta-full-width>
<div id="content" class="container">
  <h3>Nitrile Butadiene Rubber (NBR)</h3>
  <p><a [routerLink]="['/buna-n-o-rings.htm']">Nitrile Butadiene Rubber</a> (NBR) is common in several applications due
    to its low cost and comprehensive benefits.
    NBR can be a potential solution for harsh environments, such as extremely abrasive applications. </p>

  <h4>Benefits of NBR</h4>
  <ul class="responsive-grid-list">
    <li>Excellent abrasion resistance.</li>
    <li>Good tear resistance.</li>
    <li>Good water resistance.</li>
    <li>Cheaper than fluoroelastomers.</li>
    <li>Good oil and fuel resistance.</li>
    <li>Good compression set.</li>
    <li>Good non-polar solvent resistance.</li>
    <li>Good oil resistance.</li>
    <li>Good low temperature resistance to -65°F (-54°C).</li>
    <li>Cost-effectiveness.</li>
  </ul>

  <h3>Ethylene Propylene (EPDM)</h3>
  <p><a [routerLink]="['/epdm-o-rings.htm']">Ethylene Propylene</a> (EPDM) is an extremely durable copolymer that offers
    some of the best cold temperature
    resistance that can be found. </p>

  <h4>Benefits of EPDM</h4>
  <ul class="responsive-grid-list">
    <li>Excellent resistance to ozone, sunlight, and weathering.</li>
    <li>Very good compression.</li>
    <li>Adherence to metal.</li>
    <li>Works well with adhesives.</li>
    <li>Resistance to tears, abrasion, and steam.</li>
    <li>Ability to withstand extremely cold temperatures, with an operating temperature as low as -58°F (-50°C) (with
      highs of 302°F (150°C)).</li>
  </ul>
  <h3>Custom Rubber Molding Tolerances</h3>
  <p>To maximize performance, tight tolerances are critical. Tight tolerances lead to higher precision and a better fit.
    In applications that require a seamless fit, this is even more important.</p>
  <p>As rubber is flexible in nature, tighter tolerances can take longer to achieve, but our team is more than capable
    of it. If you let us know your tolerances, we can work with you to make it happen. </p>
  <p>In general, you can use the following guide to set expectations for tolerances. Note: “fixed dimension” means an
    independent dimension that is not calculated from other reference dimensions. A “closure dimension” is the largest
    outside dimension and additional outside dimensions on the same part.</p>
  <ag-grid-component [defaultColDefOverrides]="{floatingFilter: false, sortable: false}"
    [additionalHeaderDefs]="customMoldedAddHeaders" [rowData]="customMoldedRowData"
    [colDefs]="customMoldedColDefs"></ag-grid-component>
  <br />
  <h3>Pre-Production Samples of Custom Molded Rubber Parts</h3>
  <p>Our first priority is your satisfaction. We believe that comes from an effective, long-lasting seal and an
    outstanding experience. One way we ensure you have the best experience is by providing a pre-production sample of
    our custom molded rubber parts. </p>
  <p>While we rigorously test the custom components, we can send you a small quantity to test and approve yourself. This
    way, you can demonstrate excellence in your application before you invest in a whole order.</p>
  <p>We also offer custom packaging, labeling, and kitting to go the extra step. These services increase productivity
    and decrease part confusion. </p>
  <p>Together, we can find the answer to your complex seal. Along the way, we can make your life easier in more ways
    than one. </p>
  <p>As a collaborative sealing partner, when you succeed - we succeed.</p>
</div>

<app-cta-full-width></app-cta-full-width>

<div id="content" class="container">

  <h3>Marco Rubber's Custom Molded Rubber Seals At Work</h3>
  <p>Do custom molded rubber seals actually make a difference? Explore the following case studies to see how our custom
    solutions revolutionized these operations.</p>
  <app-blog-links [columns]="'3'" [cards]="['custom-rubber', 'custom-sealing', 'developing-seal']"></app-blog-links>
</div>